import axios from 'axios';
import { captureApiError } from '@/lib/telemetry';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3101/api';
const ACCESS_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';

let refreshPromise: Promise<string> | null = null;

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 60000,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_URL}/auth/refresh`, { refresh_token: refreshToken }, { timeout: 15000 })
      .then(({ data }) => {
        localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
        if (data.user?.email) {
          localStorage.setItem('user_email', data.user.email);
        }
        return data.access_token as string;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

function clearAuthAndRedirect() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('user_email');
  window.location.href = '/login?expired=true';
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';
    const isAuthRequest = url.includes('/auth/login') || url.includes('/auth/refresh');

    if (error.response?.status === 401 && originalRequest && !isAuthRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${accessToken}`,
        };
        return apiClient(originalRequest);
      } catch {
        clearAuthAndRedirect();
      }
    } else if (error.response?.status === 401 && !isAuthRequest) {
      clearAuthAndRedirect();
    } else {
      // Capture degraded conditions — non-401, non-auth API errors that the UI
      // will handle (e.g. show an error message) but that would otherwise be
      // invisible to Sentry. 401s are excluded because they drive normal
      // session refresh / redirect flows, not signal unexpected failures.
      const status: number | undefined = error.response?.status;
      const url: string = originalRequest?.url ?? 'unknown';
      captureApiError(error, { url, status });
    }

    return Promise.reject(error);
  },
);
